import React from "react";
import { Comment } from "../../firebase/comments";
import { format } from "date-fns";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Avatar from "../shared/Avatar";
import Likes from "./Likes";
import Delete from "./Delete";
import ProfileLink from "../shared/ProfileLink";
import CommentType from "./CommentType";
import Flag from "./Flag";
import { makeStyles } from "@material-ui/core/styles";
import { colors } from "../../theme";

import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Divider from "@material-ui/core/Divider";
import { useAuth } from "../../firebase/AuthProvider";

interface Props {
  comments: Comment[];
  flagComment: (comment: Comment, flag: boolean) => Promise<void>;
  commentsFlaggedByUser: string[];
}

const useStyles = makeStyles((theme) => ({
  commentGrid: {
    paddingLeft: "30px",
    width: "100%",
  },
  comment: {
    marginTop: "10px",
    width: "100%",
    display: "flex",
  },
  commentAvatar: {
    marginTop: "15px",
  },
  commentTop: {
    display: "flex",
    borderTop: "1px solid " + colors.COMMENT_BORDER,
    paddingTop: "20px",
    width: "100%",
    height: "45px"
  },
  commentDate: {
    fontSize: "14px",
    color: colors.TEXT_SECONDARY,
    width: "90%",
  },
  commentOptions: {
    width: "10%",
    position: "relative",
  },
  moreHorizon: {
    color: colors.COMMENT_BORDER
  },
  commentTooltip: {
    width: "100%",
  },
  commentList: {
    position: "absolute",
    top: "45px",
    left: "-15px",
    backgroundColor: colors.BACKGROUND_SECONDARY,
    border: "1px solid",
    borderColor: colors.TEXT_SECONDARY,
    borderRadius: "10px",
  },
  profileLink: {
    color: colors.TEXT_PRIMARY,
  },
  groupComment: {
    fontSize: "16px",
    color: colors.TEXT_SECONDARY,
  },
  listButton: {
    "&:hover": {
      backgroundColor: colors.BACKGROUND_SECONDARY
    }
  }
}));

export default function CommentList({
  comments,
  flagComment,
  commentsFlaggedByUser,
}: Props) {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [openCommentId, setId] = React.useState();
  const { user, isAdmin } = useAuth();

  function parseText(text: string) {
    return text
      .split(/\r\n|\r|\n/)
      .filter((line) => line !== "")
      .map((line: string) => <p>{line}</p>);
  }

  async function showOptions(id: any) {
    setOpen(!open);
    setId(id);
  }

  return (
    <>
      {comments.map((comment) => (
        <div key={comment.id} className={classes.comment}>
          <Grid container sm xs={12} spacing={2}>
            <Grid item xs={2} sm={1} className={classes.commentAvatar}>
              <ProfileLink userId={comment.author.userId}>
                <Avatar>{comment.author.avatar}</Avatar>
              </ProfileLink>
            </Grid>
            <Grid item xs={10} sm={11} container>
              <div className={classes.commentGrid}>
                <div className={classes.commentTop}>
                  <Typography
                    component="p"
                    variant="body1"
                    className={classes.commentDate}
                  >
                    {format(comment.timestamp.toDate(), "dd/MM/yyyy - HH:mm")}
                  </Typography>
                  <div className={classes.commentOptions}>
                    <Tooltip title="More" className={classes.commentTooltip} disableFocusListener disableTouchListener>
                      <IconButton
                        aria-label="more"
                        className={classes.moreHorizon}
                        onClick={async () => await showOptions(comment.id)}
                      >
                        <MoreHorizIcon />
                      </IconButton>
                    </Tooltip>
                    {open && openCommentId === comment.id && (
                      <List component="nav" className={classes.commentList}>
                        <ListItem className={classes.listButton} button>
                          <Flag
                            flagged={commentsFlaggedByUser.includes(comment.id)}
                            flag={async () => await flagComment(comment, true)}
                            unflag={async () => await flagComment(comment, false)}
                          />
                        </ListItem>
                        {(comment.author.userId === user?.id || isAdmin) && (
                          <>
                            <Divider />
                            <ListItem button className={classes.listButton}>
                              <Delete comment={comment} />
                            </ListItem>
                          </>
                        )}
                      </List>
                    )}
                  </div>
                </div>
                <Typography component="p" variant="body1">
                  <ProfileLink userId={comment.author.userId}>
                    <b className={classes.profileLink}>
                      {comment.author.displayname}
                    </b>
                  </ProfileLink>
                  <Typography
                    component="p"
                    variant="body1"
                    className={classes.groupComment}
                  >
                    {comment.author.groupAccount && <>group comment</>}
                  </Typography>
                  {parseText(comment.text)}
                  <div style={{ display: "flex" }}>
                    <div>
                      <Likes
                        commentId={comment.id}
                        authorUserId={comment.author.userId}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginLeft: "15px",
                      }}
                    >
                      <CommentType commentType={comment.type} />
                    </div>
                  </div>
                </Typography>
              </div>
            </Grid>
          </Grid>
        </div>
      ))}
    </>
  );
}
